import React, { ChangeEvent, MouseEvent, useEffect, useState, SyntheticEvent } from 'react';

// ** MUI Imports
import { useTheme, useMediaQuery, CardActions, Alert, Switch, Typography, Divider, TextField, Button, Box, Card, Modal, Paper, Grid, FormControl, MenuItem, InputLabel, Select, CardContent, Chip, Stack } from '@mui/material';
import { Add as AddIcon, AddAlert as AddAlertIcon, Edit as EditIcon, Delete as DeleteIcon } from '@mui/icons-material';

import { Link, NavLink, useNavigate, useParams } from 'react-router-dom';
import Title from '../../../components/Title';
import axios from 'axios';
import { useSelection } from '../../../components/context/SelectionContext';

import { Collect } from '../../../models/collect';
import { Office } from '../../../models/office';
import { FormControlLabel } from '@mui/material';
import HeaderResponsive from '../../../components/layouts/HeaderResponsive';
import { stickyListRegionWidth } from '../../../components/layouts/StickyListToolbar';
import { useDrawer } from '../../../components/context/DrawerContext';
import PermissionButton from '../../../components/common/PermissionButton';
import { useResourcePermissions } from '../../../hooks/useResourcePermissions';

interface ShowAlerts {
  showAlert: (message: string, severity?: 'success' | 'info' | 'warning' | 'error') => void;
  showLoading: () => void;
  hideLoading: () => void;
}

interface State {
  password: string;
  password2: string;
  showPassword: boolean;
  showPassword2: boolean;
}

type MobileDevice = {
  id: number;
  installation_id: string;
  status: string;
  device_name?: string | null;
  platform?: string | null;
  brand?: string | null;
  model?: string | null;
  app_version?: string | null;
  last_login_at?: string | null;
  last_seen_at?: string | null;
};

type MobileDeviceRequest = {
  id: number;
  installation_id: string;
  status: string;
  device_name?: string | null;
  platform?: string | null;
  app_version?: string | null;
  request_reason?: string | null;
  review_note?: string | null;
  requested_at?: string | null;
  reviewed_at?: string | null;
};

const EmpleadosEdit: React.FC<ShowAlerts> = ({ showAlert, showLoading, hideLoading }) => {
  document.title = 'Editar Empleado - Mis Rutas';
  const { drawerOpen } = useDrawer();
  const { canCreate, canUpdate, canDelete } = useResourcePermissions('/administrar/empleados');

  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const navigate = useNavigate();
  const [idnumber, setIdnumber] = useState('');
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');
  const [cellphone, setCellphone] = useState('');
  const [address, setAddress] = useState('');
  const [password, setPassword] = useState('');
  const [password_confirm, setPassword_confirm] = useState('');
  const [first_name, setFirst_name] = useState('');
  const [last_name, setLast_name] = useState('');
  const [username, setUsername] = useState('');
  const [initialUsername, setInitialUsername] = useState('');
  const [mobileDevices, setMobileDevices] = useState<MobileDevice[]>([]);
  const [mobileDeviceRequests, setMobileDeviceRequests] = useState<MobileDeviceRequest[]>([]);
  const [collectsOffice, setCollect_idOffice] = useState([]);
  const [lends, setLends] = useState([]);
  const [offices, setOffices] = useState([]);
  const [updatePassword, setUpdatePassword] = useState(false); // Estado para el Switch
  const [isModified, setIsModified] = useState(false); // Estado para rastrear modificaciones
  const { id } = useParams();
  const { office_id, setOffice_id, collect_id, setCollect_id, user_id, setUser_id } = useSelection();

  const loadMobileDevices = async () => {
    try {
      const {data} = await axios.get(`employees/${id}/mobile-devices`);
      setMobileDevices(Array.isArray(data) ? data : []);
    } catch (error) {
      console.error('Error fetching mobile devices:', error);
    }
  };

  const loadMobileDeviceRequests = async () => {
    try {
      const {data} = await axios.get(`employees/${id}/mobile-device-requests`);
      setMobileDeviceRequests(Array.isArray(data) ? data : []);
    } catch (error) {
      console.error('Error fetching mobile device requests:', error);
    }
  };

  useEffect(() => {
    const interceptor = axios.interceptors.response.use(
      (response) => {
        return response;
      },
      (error) => {
        if (error.response && error.response.status === 401) {
          navigate('/login');
          return; // Salimos temprano y no devolvemos el error rechazado.
        }
      }
    );
    return () => {
      axios.interceptors.response.eject(interceptor);
    };
  }, [navigate]);

  useEffect(() => {
    const interceptor = axios.interceptors.response.use(
      (response) => {
        return response;
      },
      (error) => {
        if (error.response && error.response.status === 401) {
          navigate('/login');
          return; // Salimos temprano y no devolvemos el error rechazado.
        }
      }
    );
    return () => {
      axios.interceptors.response.eject(interceptor);
    };
  }, [navigate]);

  useEffect(() => {
    (async () => {
      try {
        showLoading();
        const { data } = await axios.get(`employees/${id}`);
        setIdnumber(data.idnumber);
        setFirst_name(data.first_name);
        setLast_name(data.last_name);
        setUsername(data.username);
        setInitialUsername(data.username);
        setPhone(data.phone);
        setCellphone(data.cellphone);
        setAddress(data.address);
        await new Promise((response) => setTimeout(response, 1000));
        showAlert('Se han cargado los registros exitosamente!', 'info');
      } catch (error) {
        console.error('Error fetching data:', error);
        showAlert('Error fetching data!', 'error');
      } finally {
        hideLoading();
      }
    })();
  }, []);

  useEffect(() => {
    (async () => {
      await loadMobileDevices();
    })();
  }, [id]);

  useEffect(() => {
    (async () => {
      await loadMobileDeviceRequests();
    })();
  }, [id]);

  const handleInputChange = (setter: React.Dispatch<React.SetStateAction<any>>) => (event: ChangeEvent<HTMLInputElement>) => {
    setter(event.target.value);
    setIsModified(true);
  };

  const submit = async (event: SyntheticEvent) => {
    event.preventDefault();
    showLoading();

    if (!idnumber || !first_name || !last_name || !cellphone) {
      showAlert('Tiene campos pendientes por diligenciar', 'warning');
      hideLoading();
      return;
    }

    if (updatePassword && values.password !== values.password2) {
      showAlert('Las contraseñas no coinciden', 'warning');
      hideLoading();
      return;
    }

    const formData = {
      idnumber,
      first_name,
      last_name,
      username,
      phone,
      email,
      password: updatePassword ? values.password : undefined, // Usar values.password
      cellphone,
      address,
    };

    const url = `employees/${id}`;
    try {
      const normalizedUsername = username.trim();
      if (!normalizedUsername) {
        showAlert('El nombre de usuario es obligatorio', 'warning');
        hideLoading();
        return;
      }

      if (normalizedUsername !== initialUsername.trim()) {
        const checkUsernameResponse = await axios.get(`users/check-username/${normalizedUsername}`);
        if (checkUsernameResponse.data.exists) {
          showAlert('El nombre de usuario ya está en uso', 'warning');
          hideLoading();
          return;
        }
      }

      await axios.put(url, formData);
      showAlert('Información actualizada correctamente!', 'info');
      await new Promise((resolve) => setTimeout(resolve, 3000));
      navigate('/administrar/empleados');
    } catch (error: any) {
      showAlert('Error fetching data!', 'error');
      console.error('Error:', error);
    } finally {
      hideLoading();
    }
  };

  // ** States
  const [values, setValues] = useState<State>({
    password: '',
    password2: '',
    showPassword: false,
    showPassword2: false,
  });

  // Handle Password
  const handlePasswordChange = (prop: keyof State) => (event: ChangeEvent<HTMLInputElement>) => {
    setValues({ ...values, [prop]: event.target.value });
    setIsModified(true);
  };

  // Handle Confirm Password
  const handleConfirmChange = (prop: keyof State) => (event: ChangeEvent<HTMLInputElement>) => {
    setValues({ ...values, [prop]: event.target.value });
    setIsModified(true);
  };

  useEffect(() => {
    const fetchOffices = async () => {
      try {
        const { data } = await axios.get('offices');
        setOffices(data.data);
      } catch (error) {
        console.error('Error fetching offices:', error);
      }
    };

    const fetchCollectsOffice = async (officeId: string) => {
      try {
        const { data } = await axios.get(`collectsoffice/${officeId}`);
        setCollect_idOffice(data); // Asegúrate de que `data` tenga el formato correcto
      } catch (error) {
        console.error('Error fetching collects office:', error);
      }
    };

    fetchOffices();
    if (office_id) {
      fetchCollectsOffice(office_id);
    }
  }, [office_id]);

  const handleUpdatePasswordChange = () => {
    setUpdatePassword(!updatePassword);
    setPassword('');
    setPassword_confirm('');
    setIsModified(true);
  };

  const handleMobileDeviceStatusChange = async (
    deviceId: number,
    status: 'approved' | 'blocked'
  ) => {
    try {
      showLoading();
      const {data} = await axios.put(`mobile-devices/${deviceId}/status`, {
        status,
      });
      await loadMobileDevices();
      showAlert(data.message || 'Estado del dispositivo actualizado.', 'success');
    } catch (error: any) {
      console.error('Error updating mobile device status:', error);
      showAlert(
        error?.response?.data?.message || 'No fue posible actualizar el estado del dispositivo.',
        'error'
      );
    } finally {
      hideLoading();
    }
  };

  const handleMobileDeviceRequestStatusChange = async (
    requestId: number,
    status: 'approved' | 'rejected'
  ) => {
    try {
      showLoading();
      const {data} = await axios.put(`mobile-device-requests/${requestId}/status`, {
        status,
      });
      await loadMobileDeviceRequests();
      await loadMobileDevices();
      showAlert(data.message || 'Solicitud actualizada correctamente.', 'success');
    } catch (error: any) {
      console.error('Error updating mobile device request status:', error);
      showAlert(
        error?.response?.data?.message || 'No fue posible actualizar la solicitud.',
        'error'
      );
    } finally {
      hideLoading();
    }
  };

  return (
    <HeaderResponsive>
      <Box display="flex" justifyContent="center" mb={1}>
        <Box
          display="flex"
          justifyContent="space-between"
          alignItems="center"
          sx={{
            width: (t) => stickyListRegionWidth(t, drawerOpen),
            maxWidth: '100%',
            boxSizing: 'border-box',
          }}
        >
          <Title>Editar Información Empleados</Title>
        </Box>
      </Box>
      <Card>
        <CardContent
          sx={{
            width: (t) => stickyListRegionWidth(t, drawerOpen),
            maxWidth: '100%',
            boxSizing: 'border-box',
            overflowX: 'auto',
          }}
        >
          <Divider sx={{ margin: 0 }} />
          <Box onSubmit={(e) => e.preventDefault()}>
            <CardContent>
              <Grid container spacing={2}>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label="Número Identificación"
                    placeholder="123456789"
                    id="idnumber"
                    size='small'
                    value={idnumber}
                    onChange={handleInputChange(setIdnumber)}
                    inputProps={{ maxLength: 20, readOnly: true }}
                    required
                    autoComplete="off"
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    type="Usuario"
                    label="Usuario"
                    size='small'
                    placeholder="Usuario"
                    id="usuario"
                    value={username}
                    onChange={handleInputChange(setUsername)}
                    inputProps={{ maxLength: 30 }}
                    required
                    autoComplete="off"
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label="Nombres"
                    size='small'
                    placeholder="Leonard"
                    id="firstname"
                    value={first_name}
                    onChange={handleInputChange(setFirst_name)}
                    inputProps={{ maxLength: 30 }}
                    required
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label="Apellidos"
                    size='small'
                    placeholder="Carter"
                    id="lastname"
                    value={last_name}
                    onChange={handleInputChange(setLast_name)}
                    inputProps={{ maxLength: 30 }}
                    required
                    autoComplete="off"
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    type="tel"
                    size='small'
                    label="Teléfono"
                    placeholder="6011234567"
                    id="phone"
                    value={phone}
                    onChange={handleInputChange(setPhone)}
                    inputProps={{ maxLength: 10 }}
                    autoComplete="off"
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    type="tel"
                    size='small'
                    label="Celular"
                    placeholder="3101234567"
                    id="cellphone"
                    value={cellphone}
                    onChange={handleInputChange(setCellphone)}
                    inputProps={{ maxLength: 10 }}
                    required
                    autoComplete="off"
                  />
                </Grid>
                {/* <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Dirección"
                  placeholder="Cl 10 10-10"
                  id="address"
                  size='small'
                  value={address}
                  onChange={handleInputChange(setAddress)}
                  inputProps={{ maxLength: 30 }}
                  required
                  autoComplete="off"
                />
              </Grid>              
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  type="email"
                  label="Email"
                  size='small'
                  placeholder="correoa@gmail.com"
                  onChange={(event) => setEmail(event.target.value)}
                  inputProps={{ maxLength: 50, autoComplete: "off" }}
                  autoComplete="off"
                />
              </Grid> */}
                <Grid item xs={12} sm={6}>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={updatePassword}
                        onChange={handleUpdatePasswordChange}
                        inputProps={{ 'aria-label': 'controlled' }}
                      />
                  }
                  label="Actualizar Contraseña"
                />
                </Grid>
                <Grid item xs={12}>
                  <Divider sx={{ my: 1 }} />
                  <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 2 }}>
                    Dispositivos Mobile Registrados
                  </Typography>
                  {mobileDevices.length === 0 ? (
                    <Typography variant="body2" color="text.secondary">
                      No hay dispositivos registrados para este empleado.
                    </Typography>
                  ) : (
                    <Stack spacing={2}>
                      {mobileDevices.map((device) => {
                        const statusColor =
                          device.status === 'approved'
                            ? 'success'
                            : device.status === 'blocked'
                              ? 'error'
                              : 'warning';

                        return (
                          <Paper
                            key={device.id}
                            variant="outlined"
                            sx={{ p: 2, borderRadius: 2 }}
                          >
                            <Box
                              display="flex"
                              justifyContent="space-between"
                              alignItems="flex-start"
                              gap={2}
                              flexWrap="wrap"
                            >
                              <Box>
                                <Typography variant="subtitle2">
                                  {device.device_name || 'Dispositivo sin nombre'}
                                </Typography>
                                <Typography variant="body2" color="text.secondary">
                                  ID instalación: {device.installation_id}
                                </Typography>
                                <Typography variant="body2" color="text.secondary">
                                  Plataforma: {device.platform || 'N/D'} | App: {device.app_version || 'N/D'}
                                </Typography>
                                <Typography variant="body2" color="text.secondary">
                                  Último login: {device.last_login_at || 'N/D'}
                                </Typography>
                                <Typography variant="body2" color="text.secondary">
                                  Última actividad: {device.last_seen_at || 'N/D'}
                                </Typography>
                              </Box>
                              <Box display="flex" gap={1} alignItems="center" flexWrap="wrap">
                                <Chip
                                  label={
                                    device.status === 'approved'
                                      ? 'Aprobado'
                                      : device.status === 'blocked'
                                        ? 'Bloqueado'
                                        : 'Pendiente'
                                  }
                                  color={statusColor as any}
                                  size="small"
                                />
                                <Button
                                  size="small"
                                  variant="contained"
                                  onClick={() => handleMobileDeviceStatusChange(device.id, 'approved')}
                                  disabled={device.status === 'approved'}
                                >
                                  Aprobar
                                </Button>
                                <Button
                                  size="small"
                                  variant="outlined"
                                  color="error"
                                  onClick={() => handleMobileDeviceStatusChange(device.id, 'blocked')}
                                  disabled={device.status === 'blocked'}
                                >
                                  Bloquear
                                </Button>
                              </Box>
                            </Box>
                          </Paper>
                        );
                      })}
                    </Stack>
                  )}
                </Grid>
                <Grid item xs={12}>
                  <Divider sx={{ my: 1 }} />
                  <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 2 }}>
                    Solicitudes de Dispositivo
                  </Typography>
                  {mobileDeviceRequests.length === 0 ? (
                    <Typography variant="body2" color="text.secondary">
                      No hay solicitudes registradas para este empleado.
                    </Typography>
                  ) : (
                    <Stack spacing={2}>
                      {mobileDeviceRequests.map((request) => {
                        const statusColor =
                          request.status === 'approved'
                            ? 'success'
                            : request.status === 'rejected'
                              ? 'error'
                              : 'warning';

                        return (
                          <Paper
                            key={request.id}
                            variant="outlined"
                            sx={{ p: 2, borderRadius: 2 }}
                          >
                            <Box
                              display="flex"
                              justifyContent="space-between"
                              alignItems="flex-start"
                              gap={2}
                              flexWrap="wrap"
                            >
                              <Box>
                                <Typography variant="subtitle2">
                                  Solicitud #{request.id}
                                </Typography>
                                <Typography variant="body2" color="text.secondary">
                                  Dispositivo: {request.device_name || 'Sin nombre'}
                                </Typography>
                                <Typography variant="body2" color="text.secondary">
                                  ID instalación: {request.installation_id}
                                </Typography>
                                <Typography variant="body2" color="text.secondary">
                                  Plataforma: {request.platform || 'N/D'} | App: {request.app_version || 'N/D'}
                                </Typography>
                                <Typography variant="body2" color="text.secondary">
                                  Solicitada: {request.requested_at || 'N/D'}
                                </Typography>
                                {request.request_reason ? (
                                  <Typography variant="body2" color="text.secondary">
                                    Motivo: {request.request_reason}
                                  </Typography>
                                ) : null}
                                {request.review_note ? (
                                  <Typography variant="body2" color="text.secondary">
                                    Nota revisión: {request.review_note}
                                  </Typography>
                                ) : null}
                              </Box>
                              <Box display="flex" gap={1} alignItems="center" flexWrap="wrap">
                                <Chip
                                  label={
                                    request.status === 'approved'
                                      ? 'Aprobada'
                                      : request.status === 'rejected'
                                        ? 'Rechazada'
                                        : 'Pendiente'
                                  }
                                  color={statusColor as any}
                                  size="small"
                                />
                                <Button
                                  size="small"
                                  variant="contained"
                                  onClick={() => handleMobileDeviceRequestStatusChange(request.id, 'approved')}
                                  disabled={request.status === 'approved'}
                                >
                                  Aprobar
                                </Button>
                                <Button
                                  size="small"
                                  variant="outlined"
                                  color="error"
                                  onClick={() => handleMobileDeviceRequestStatusChange(request.id, 'rejected')}
                                  disabled={request.status === 'rejected'}
                                >
                                  Rechazar
                                </Button>
                              </Box>
                            </Box>
                          </Paper>
                        );
                      })}
                    </Stack>
                  )}
                </Grid>
                {updatePassword && (
                  <Grid item xs={12}>
                    <Grid container spacing={2}>
                      <Grid item xs={12} sm={6}>
                        <TextField
                          fullWidth
                          size='small'
                          label="Contraseña"
                          type={values.showPassword ? 'text' : 'password'}
                          placeholder="Password"
                          onChange={handlePasswordChange('password')}
                          value={values.password}
                          inputProps={{ maxLength: 30, autoComplete: "off" }}
                          required
                          autoComplete="off"
                        />
                      </Grid>
                      <Grid item xs={12} sm={6}>
                        <FormControl fullWidth>
                          <TextField
                            fullWidth
                            size='small'
                            label="Confirmar Contraseña"
                            type={values.showPassword2 ? 'text' : 'password'}
                            placeholder="Confirmar Contraseña"
                            onChange={handleConfirmChange('password2')}
                            value={values.password2}
                            inputProps={{ maxLength: 30, autoComplete: "off" }}
                            required
                            autoComplete="off"
                          />
                        </FormControl>
                      </Grid>
                    </Grid>
                  </Grid>
                )}
              </Grid>
            </CardContent>
            <Divider sx={{ margin: 0 }} />
            <CardActions>
              <Button
                size="small"
                type="submit"
                sx={{ mr: 2 }}
                variant="contained"
                onClick={submit}
                disabled={!isModified} // Deshabilitar el botón si no ha habido modificaciones
              >
                Guardar
              </Button>
              <Link to="/administrar/empleados">
                <Button size="small" color="secondary" variant="outlined">
                  Cancelar
                </Button>
              </Link>
            </CardActions>
          </Box>
        </CardContent>
      </Card>
    </HeaderResponsive >

  );
};

export default EmpleadosEdit;
